import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { User, LogOut, Menu } from 'lucide-react';
import NotificationIcon from './NotificationIcon';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const Header = () => {
  const { user, signOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const menuItems = [
    { title: 'Home', path: '/' },
    { title: 'Services', path: '/services' },
    { title: 'About', path: '/about' },
    { title: 'Contact', path: '/contact' },
  ];

  const handleNavigate = (path: string) => {
    setIsOpen(false);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      setIsOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
      // You might want to show an error message to the user here
    }
  };

  return (
    <header className="bg-white shadow-sm border-b animate-fade-in sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14 sm:h-16">
          <div className="flex items-center">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="sm" className="mr-2">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[240px] sm:w-[300px] p-0">
                <div className="flex flex-col h-full">
                  <div className="p-4 border-b">
                    <img 
                      src="/brand-assets/Sigma%20Pl%20-%20Black.png" 
                      alt="Sigma Immigration Logo" 
                      className="h-8 w-auto"
                    />
                  </div>
                  <nav className="flex-1 p-4">
                    <div className="flex flex-col space-y-1">
                      {menuItems.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          className="flex items-center px-4 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-100 hover:text-sigma-red transition-colors duration-200"
                          onClick={() => handleNavigate(item.path)}
                        >
                          {item.title}
                        </Link>
                      ))}
                    </div>
                  </nav>
                  {user ? (
                    <div className="p-4 border-t">
                      <Link 
                        to="/dashboard" 
                        className="flex items-center px-4 py-2 text-sm font-medium text-sigma-red rounded-md hover:bg-red-50 transition-colors duration-200 mb-2"
                        onClick={() => setIsOpen(false)}
                      >
                        <User className="h-4 w-4 mr-2" />
                        Dashboard
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm font-medium text-gray-600 rounded-md hover:bg-gray-100 transition-colors duration-200"
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Logout
                      </button>
                    </div>
                  ) : (
                    <div className="p-4 border-t space-y-2">
                      <Link 
                        to="/login" 
                        className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-sigma-red border border-sigma-red rounded-md hover:bg-red-50 transition-colors duration-200"
                        onClick={() => setIsOpen(false)}
                      >
                        Login
                      </Link>
                      <Link 
                        to="/register" 
                        className="flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-white bg-sigma-red rounded-md hover:bg-sigma-dark-red transition-colors duration-200"
                        onClick={() => setIsOpen(false)}
                      >
                        Register
                      </Link>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
            <Link to="/" className="flex items-center transition-transform duration-200 hover:scale-105">
              <img 
                src="/brand-assets/Sigma%20Pl%20-%20Black.png" 
                alt="Sigma Immigration Logo" 
                className="h-8 sm:h-10 w-auto"
              />
            </Link>
          </div>
          
          <nav className="hidden md:flex space-x-6">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="text-sigma-black hover:text-sigma-red transition-colors duration-200 font-hind text-sm"
              >
                {item.title}
              </Link>
            ))}
          </nav>

          <div className="flex items-center space-x-2 sm:space-x-4">
            {user ? (
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className="hidden sm:block">
                  <NotificationIcon />
                </div>
                <Link to="/dashboard">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="hidden sm:flex transition-all duration-200 hover:scale-105 border-sigma-red text-sigma-red hover:bg-sigma-red hover:text-white"
                  >
                    <User className="h-4 w-4 mr-2" />
                    Dashboard
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="sm:hidden w-9 h-9 p-0 border-sigma-red text-sigma-red hover:bg-sigma-red hover:text-white"
                  >
                    <User className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="hidden sm:flex items-center space-x-2">
                <Link to="/login">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="transition-all duration-200 hover:scale-105 border-sigma-red text-sigma-red hover:bg-sigma-red hover:text-white"
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    size="sm" 
                    className="transition-all duration-200 hover:scale-105 bg-sigma-red hover:bg-sigma-dark-red text-white"
                  >
                    Register
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
export { Header };
